import {Directive, forwardRef, Input, OnChanges, OnInit} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Observable} from 'rxjs';
import {TypedSimpleChanges} from '../../../common/typed-on-changes';
import {
    AutocompleteConfig,
    AutocompleteDirective,
    AutocompleteProviderConfig,
    AutocompleteSearchType
} from '../../autocomplete';
import {Deliverable} from '../../dex/dex-streaming/types/deliverable';
import {DeliverableAutocompleteListComponent} from './deliverable-autocomplete-list.component';
import {DeliverableAutocompleteService} from './deliverable-autocomplete.service';

@Directive({
    selector: '[deliverableAutocomplete]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DeliverableAutocompleteDirective),
            multi: true
        }
    ],
    standalone: false
})
export class DeliverableAutocompleteDirective extends AutocompleteDirective<Deliverable>
    implements ControlValueAccessor, OnChanges, OnInit {

    @Input('deliverableAutocomplete')
    public inputConfig: AutocompleteConfig;

    public override listComponent: DeliverableAutocompleteListComponent;

    protected override readonly listComponentClass = DeliverableAutocompleteListComponent;

    protected readonly autocompleteProviderService = this.injector.get(DeliverableAutocompleteService);

    private autocompleteItems: Deliverable[];

    public ngOnChanges(changes: TypedSimpleChanges<DeliverableAutocompleteDirective>): void {
        if (changes.inputConfig && !changes.inputConfig.firstChange) {
            this.updateConfig();
        }
    }

    public override ngOnInit(): void {
        this.updateConfig();

        super.ngOnInit();
    }

    protected override getRawVariantsByTerm(term: string): Observable<Deliverable[]> {
        return this.autocompleteProviderService.autocomplete(
            term,
            this.config.type as AutocompleteProviderConfig<any>
        );
    }

    protected override transformRawVariants(items: Deliverable[]): Deliverable[] {
        this.autocompleteItems = items; // Store the raw autocomplete items in order to reuse it in the list component.

        return items;
    }

    protected override open(): void {
        super.open();

        this.listComponent.term$ = this.term$;
    }

    protected override setListComponentVariants(): void {
        this.listComponent.setVariants(this.variants, this.autocompleteItems);
    }

    private updateConfig(): void {
        const inputConfig = this.inputConfig || {};
        const inputConfigType: AutocompleteSearchType<Deliverable> = inputConfig.type;
        const filters = inputConfigType && (inputConfigType as AutocompleteProviderConfig<Deliverable>).filters
            || undefined;

        this.config = {
            freeTyping:                false,
            disallowEmptySelect:       true,
            selectAllOnFocus:          true,
            allowVariantsForEmptyTerm: true,

            ...inputConfig,

            type: {
                filters,
                provider: this.autocompleteProviderService
            },

            displayValue: (d: Deliverable) => d.symbol
        };
    }

}
